import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import { Quote, ArrowRight } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5";

const TITLE = "Customers — what contractors say | Send-Proof";
const DESC = "Renovation, roofing and pool crews use Send-Proof to text a punch list, get photo proof back, and stop driving to sites to check on subs.";

// Real customer quotes. TODO(owner): add a headshot URL to each `photo` to strengthen.
const TESTIMONIALS = [
  { quote: "I couldn't stop one job just to drive across town and see if the electrician had actually finished. Now the proof photos land on my phone, I know it's done, and I keep working the job I'm on.", name: "Ryan Trexler", role: "Project manager", photo: "" },
  { quote: "So much easier when it's just one feature — not a whole company. The app is awesome.", name: "Uli", role: "Renovation company owner", photo: "" },
  { quote: "Pretty cool, and very easy to use. Saves me so much time driving around to check on jobs.", name: "Aaron", role: "Project manager, pool & spa company", photo: "" },
  { quote: "Such a good app.", name: "Andrew", role: "Roofing company owner", photo: "" },
];

export default function Customers() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", DESC);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[700px]">
        <div className="absolute left-1/2 top-[-200px] h-[460px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="customers-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="customers-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-2xl px-5 pt-10 text-center sm:px-8 sm:pt-16" data-testid="customers-page">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">What crews say</h1>
          <p className="mx-auto mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">Send-Proof is a punch list app crews use to text a list and get photo proof back — renovation, roofing and pool crews who stopped driving to sites just to check a box.</p>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-6xl px-5 pt-12 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-3">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className={`${panel} p-6`} data-testid={`customer-${t.name}`}>
                <Quote className="h-5 w-5 text-emerald-500" />
                <p className="mt-3 text-sm text-foreground">"{t.quote}"</p>
                <div className="mt-4 flex items-center gap-3">
                  {t.photo && <img src={t.photo} alt={t.name} className="h-9 w-9 rounded-full object-cover" />}
                  <span className="text-sm font-bold">{t.name}<span className="font-normal text-muted-foreground"> · {t.role}</span></span>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pb-24 pt-24 text-center sm:px-8">
          <div className="rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-8 shadow-2xl shadow-emerald-500/20">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Join them — try it on your next punch list</h2>
            <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">15 days free, no credit card. $9.99/month after — one flat plan.</p>
            <Button size="lg" className="mt-6 h-12 bg-emerald-500 px-8 text-base hover:bg-emerald-600" data-testid="customers-cta" onClick={go}>
              Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </motion.section>

        <SiteFooter />
      </div>
    </div>
  );
}
